import { PathParams, rest } from 'msw'
import { BankAccountsDetails } from 'types/BankAccountsDetails'
import { Quote } from 'types/payments'
import { Transaction } from 'types/transaction'
import { BankAccount, Currency, CurrencyLimits, ErrorAPI, WalletType } from 'types/types'
import {
  CurrencyInterface,
  PayFee,
  WalletsPayoutsRequest,
  WalletsPayRequest,
  WalletSummary,
  WalletsWithdrawResponse,
  WalletsWithdrawResponseError,
  WalletsWithdrawValidateRequest,
  WalletsWithdrawValidateResponse,
  WalletsWithdrawValidateResponseError,
} from 'types/wallets'
import mockedSupportedCurrencies from '../../../__mocks__/Currency/supportedCurrencies.json'
import mockedTransactions from '../../../__mocks__/Transactions/transactions.json'
import mockDepositAccounts from '../../../__mocks__/Wallets/depositAccounts.json'
import mockDepositAccountsAggregated from '../../../__mocks__/Wallets/depositAccountsAggregated.json'
import mockDepositAccountsSegregated from '../../../__mocks__/Wallets/depositAccountsSegregated.json'
import mockDepositAccountsSegregatedInactiveError from '../../../__mocks__/Wallets/depositInactiveSegregatedAccount.json'
import mockCsvExportTransactions from '../../../__mocks__/Wallets/exportCsvTransansactionResponse.json'
import mockPdfExportTransactions from '../../../__mocks__/Wallets/exportPdfTransansactionResponse.json'
import mockPay from '../../../__mocks__/Wallets/pay.json'
import mockConvertCurrencies from '../../../__mocks__/Wallets/walletConvertCurrencies.json'
import mockConvertQuote from '../../../__mocks__/Wallets/walletQuote.json'
import mockWallets from '../../../__mocks__/Wallets/wallets.json'
import massPayoutsWallets from '../../../__mocks__/Wallets/wallets.json'
import mockedWallets from '../../../__mocks__/Wallets/wallets.json'
import walletSearchTransactions from '../../../__mocks__/Wallets/walletSearchResponse.json'
import mockedWalletSummary from '../../../__mocks__/Wallets/walletSummary.json'
import mockWithdraw from '../../../__mocks__/Wallets/withdraw.json'
import mockWithdrawAccounts from '../../../__mocks__/Wallets/withdrawAccounts.json'
import mockWalletsWithdrawError from '../../../__mocks__/Wallets/withdrawErrors.json'
import mockWithdrawLimits from '../../../__mocks__/Wallets/withdrawLimits.json'
import mockWithdrawValidate from '../../../__mocks__/Wallets/withdrawValidate.json'
import mockWalletsWithdrawValidateError from '../../../__mocks__/Wallets/withdrawValidateErrors.json'

const postWalletWithdrawHandler = rest.post<
  WalletsPayoutsRequest,
  PathParams,
  WalletsWithdrawResponse
>(`${API_URL}/api/wallet/withdraw/*`, async (req, res, ctx) => {
  return res(ctx.json(mockWithdraw))
})

const postWalletPayHandler = rest.post<WalletsPayRequest, PathParams, WalletsPayRequest>(
  `${API_URL}/api/wallet/send/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockPay))
  }
)

export const getWalletLimitsHandler = rest.get<CurrencyLimits>(
  `${API_URL}/api/v1/limit`,
  async (req, res, ctx) => {
    return res(ctx.json(mockWithdrawLimits))
  }
)

const getWithdrawAccountsHandler = rest.get<BankAccount[]>(
  `${API_URL}/api/payment-method`,
  async (req, res, ctx) => {
    return res(ctx.json(mockWithdrawAccounts))
  }
)
const getDepositAccountsHandler = rest.get<BankAccount[]>(
  `${API_URL}/api/wallet/funding/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockDepositAccounts))
  }
)

export const getDepositAccountsHandlerAggregated = rest.get<BankAccountsDetails>(
  `${API_URL}/api/v2/wallet/:id/funding`,
  async (req, res, ctx) => {
    return res(ctx.json(mockDepositAccountsAggregated))
  }
)
export const getDepositAccountsHandlerSegregated = rest.get<BankAccountsDetails>(
  `${API_URL}/api/v2/wallet/:id/funding`,
  async (req, res, ctx) => {
    return res(ctx.json(mockDepositAccountsSegregated))
  }
)

export const getDepositAccountsHandlerSegregatedInactiveClientError = rest.get<ErrorAPI>(
  `${API_URL}/api/v2/wallet/:id/funding`,
  async (req, res, ctx) => {
    return res(ctx.status(412), ctx.json(mockDepositAccountsSegregatedInactiveError))
  }
)

const getConvertCurrenciesHandler = rest.get<Currency[]>(
  `${API_URL}/api/wallet/*/transfer`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertCurrencies))
  }
)

const postWalletQuoteEsimateHandler = rest.post<{}, PathParams, Quote>(
  `${API_URL}/api/v1/quote`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertQuote))
  }
)

const postWalletQuoteHandler = rest.put<{}, PathParams, Quote>(
  `${API_URL}/api/v1/quote/accept/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertQuote))
  }
)

const getWalletSummary = rest.get<WalletSummary>(
  `${API_URL}/api/wallet/summary/total`,
  async (req, res, ctx) => {
    return res(ctx.json(mockedWalletSummary))
  }
)

const getValidateBankAccount = rest.get(
  `${API_URL}/api/bank/information/validation`,
  async (req, res, ctx) => {
    return res(ctx.status(200), ctx.json({}))
  }
)

const getWalletPayFee = rest.get<PayFee>(
  `${API_URL}/api/wallet/withdraw/:walletId/fee`,
  async (req, res, ctx) => {
    return res(ctx.json({ currencyCode: 'EUR', feePercentage: 0.3, fee: 5 }))
  }
)

export const getWallets = rest.get<WalletType[]>(`${API_URL}/api/wallet`, async (req, res, ctx) => {
  return res(ctx.json(mockedWallets))
})

export const getMockMassPayoutsWallets = rest.get<WalletType[]>(
  `${API_URL}/api/wallet`,
  async (req, res, ctx) => {
    return res(ctx.json(massPayoutsWallets))
  }
)

export const getAllWallets = rest.get('/api/wallet', async (req, res, ctx) => {
  return res(ctx.json(mockedWallets))
})

const getTransaction = rest.get<Transaction[]>(
  `${API_URL}/api/transaction/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockedTransactions))
  }
)

const walletsWithdrawHandlerException = rest.post<
  WalletsPayoutsRequest,
  PathParams,
  WalletsWithdrawResponseError
>(`${API_URL}/api/wallet/withdraw/*`, async (req, res, ctx) => {
  return res(ctx.status(400), ctx.json(mockWalletsWithdrawError))
})

const postWalletWithdrawValidateHandler = rest.post<
  WalletsWithdrawValidateRequest,
  PathParams,
  WalletsWithdrawValidateResponse
>(`${API_URL}/api/wallet/withdraw/validate/*`, async (req, res, ctx) => {
  return res(ctx.json(mockWithdrawValidate))
})

const walletsWithdrawValidateHandlerException = rest.post<
  WalletsWithdrawValidateRequest,
  PathParams,
  WalletsWithdrawValidateResponseError
>(`${API_URL}/api/wallet/withdraw/validate/*`, async (req, res, ctx) => {
  return res(ctx.status(400), ctx.json(mockWalletsWithdrawValidateError))
})

export const getWalletById = rest.get<WalletType>(
  `${process.env.API_URL}/api/wallet/:id`,
  async (req, res, ctx) => {
    const { id } = req.params
    const wallet = mockWallets.find(wallet => wallet.id === Number(id))
    if (wallet) {
      return res(ctx.json(wallet))
    }
    return res(ctx.json(mockWallets[0]))
  }
)
export const getWalletByIdNotFoundError = rest.get<WalletType>(
  `${process.env.API_URL}/api/wallet/:id`,
  async (req, res, ctx) => {
    return res(ctx.status(404))
  }
)

export const putChangeWalletDescription = rest.put<WalletType>(
  `${process.env.API_URL}/api/wallet/description/:id`,
  async (req, res, ctx) => {
    return res(ctx.json(mockWallets[0]))
  }
)
const walletsPayHandlerException = rest.post<
  WalletsPayoutsRequest,
  PathParams,
  WalletsWithdrawResponseError
>(`${API_URL}/api/wallet/send/*`, async (req, res, ctx) => {
  return res(ctx.status(400), ctx.json(mockWalletsWithdrawError))
})

const postDepositDetails = rest.post<WalletType>(
  `${API_URL}/api/wallet/deposit/:id`,
  async (req, res, ctx) => {
    const { id } = req.params
    const wallet = mockWallets.find(wallet => wallet.id === Number(id))
    return res(ctx.json(wallet))
  }
)
const getSupportedCurrencies = rest.get<CurrencyInterface[]>(
  `${API_URL}/api/currency/deposit`,
  async (req, res, ctx) => {
    return res(ctx.json(mockedSupportedCurrencies))
  }
)

const postWallet = rest.post<WalletType>(`${API_URL}/api/wallet`, async (req, res, ctx) => {
  return res(ctx.json(mockWallets[0]))
})

const sendWalletCsvTransactionExportToEmail = rest.get(
  `${API_URL}/api/transaction/report`,
  async (req, res, ctx) => {
    return res(ctx.json(mockCsvExportTransactions))
  }
)

const sendWalletPdfTransactionExportToEmail = rest.get(
  `${API_URL}/api/transaction/report/external`,
  async (req, res, ctx) => {
    return res(ctx.json(mockPdfExportTransactions))
  }
)

const postWalletSearch = rest.post(
  `${API_URL}/api/search/v1/transaction`,
  async (req, res, ctx) => {
    return res(ctx.json(walletSearchTransactions))
  }
)

export default [
  postWalletWithdrawHandler,
  postWalletPayHandler,
  postWalletWithdrawValidateHandler,
  postWalletQuoteEsimateHandler,
  postWalletQuoteHandler,
  postWalletSearch,
  getWalletLimitsHandler,
  getWithdrawAccountsHandler,
  getConvertCurrenciesHandler,
  getWallets,
  getAllWallets,
  getTransaction,
  getWalletSummary,
  walletsWithdrawHandlerException,
  walletsPayHandlerException,
  walletsWithdrawValidateHandlerException,
  getDepositAccountsHandlerAggregated,
  getDepositAccountsHandler,
  getWalletById,
  getWalletPayFee,
  getValidateBankAccount,
  putChangeWalletDescription,
  postDepositDetails,
  getSupportedCurrencies,
  postWallet,
  sendWalletCsvTransactionExportToEmail,
  sendWalletPdfTransactionExportToEmail,
]
